import { ChangeDetectorRef, Component, HostListener, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { CookieService } from 'ngx-cookie';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthPopinService } from '../auth-popin.service';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import {
  AuthenticationService,
  Professional,
  ProfessionalJob,
  ProfessionalJobService,
  ProfessionalPrivate,
  ProfessionalPrivateService,
  ProfessionalService,
  RegisterUser,
  User,
  UserService,
} from '@adeo/ngx-kozikaza-api';
import { CountryLegacy, CountryLegacyService as SDKCountryLegacy } from '@adeo/ngx-kozikaza-api/misc/country-legacy';
import { combineLatest, Observable, of, Subscription, throwError } from 'rxjs';
import { catchError, filter, finalize, map, switchMap, tap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { uniqueField, uniqueFieldPro } from '../../../utils/validators/unique-field.validator';
import { DataLayerService } from '../../../shared/services/utilities/data-layer.service';
import {
  emailValidators,
  firstNameValidators,
  lastNameValidators,
  usernameValidators
} from '../../../utils/const/profile';
import { AuthService } from '../auth.service';
import { brandValidators, pseudoValidators } from '../../../utils/const/pro';
import { SlugifyPipe } from '../../../utils/pipes/slugify.pipe';
import { UserStoreService } from '../../../shared/services/state-management/user-store.service';
import { handleHydraError, HydraErrorFull } from '../../../utils/forms/form-utilities';
import { UserService as LegacyUservice } from '../../../shared/services/features/user/user.service';
import { KzpService } from '../../../shared/services/utilities/kzp.service';
import { KzIconsRegistryService } from '../../../kz-ui/ui/icon/kz-icons-registry.service';
import { kzIconsDiscover } from '../../../kz-ui/ui/icon/generated-icons/kzIcons-discover.icon';
import { kzIconsKozikaza } from '../../../kz-ui/ui/icon/generated-icons/kzIcons-kozikaza.icon';
import { RegistrationNumber } from '../../../utils/validators/registration-number-valid';
import { kzIconsDiscoverDisabled } from 'src/app/kz-ui/ui/icon/generated-icons/kzIcons-discover-disabled.icon';

@Component({
  selector: 'app-signup',
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.scss'],
})
export class SignupComponent implements OnInit, OnDestroy {

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const capsOn = event.getModifierState && event.getModifierState('CapsLock');
    this.capsLock = capsOn;
  }
  @HostListener('window:keyup', ['$event'])
  onKeyUp(event: KeyboardEvent): void {
    const capsOn = event.getModifierState && event.getModifierState('CapsLock');
    this.capsLock = capsOn;
  }

  passwordFieldFocused = false;
  currentUser: User;
  currentPro: Professional;
  signupForm: UntypedFormGroup;
  submitted = false;
  hasError = false;
  initiated = false;
  error: HydraErrorFull;
  brandControl: UntypedFormControl;
  pseudoControl: UntypedFormControl;
  usernameControl: UntypedFormControl;
  mainJobControl: UntypedFormControl;
  countryControl: UntypedFormControl;
  companyRegNumberControl: UntypedFormControl;
  plainPasswordControl: UntypedFormControl;
  signupSubscription: Subscription;
  professionalFieldSubscription: Subscription;
  professionalJobSubscription: Subscription;
  emailValueChangesSubscription: Subscription;
  showPassword = false;
  userStoreSubscription: Subscription;
  emailReadOnly = false;
  public proJobsList: Array<ProfessionalJob>;
  public countriesList: Array<CountryLegacy>;
  public mainJobPlaceholder: string;

  private countriesSubscription: Subscription;

  public showInfoUsername = false;
  public showInfoLastname = false;
  public showInfoFirstname = false;
  public showInfoEmail = false;
  public showInfoPseudo = false;
  public showInfoBrand = false;
  public showInfoRegistration = false;

  capsLock = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authPopinService: AuthPopinService,
    private fb: UntypedFormBuilder,
    private authenticationService: AuthenticationService,
    private toastrService: ToastrService,
    protected dataLayerService: DataLayerService,
    // don't remove these 2 services :
    private userService: UserService,
    private authService: AuthService,
    private slugifyPipe: SlugifyPipe,
    private readonly professionalJobService: ProfessionalJobService,
    private readonly professionalService: ProfessionalService,
    private professionalPrivateService: ProfessionalPrivateService,
    public userStoreService: UserStoreService,
    private legacyUserService: LegacyUservice,
    private cookieService: CookieService,
    private readonly sdkCountryLegacyService: SDKCountryLegacy,
    private readonly kzpService: KzpService,
    private cdr: ChangeDetectorRef,
    @Optional() @Inject('DEFAULT_COUNTRY') public readonly country,
    private kzIconsRegistryService: KzIconsRegistryService,
  ) {

    this.kzIconsRegistryService.registerIcons([kzIconsDiscover, kzIconsDiscoverDisabled, kzIconsKozikaza]);
    this.mainJobPlaceholder = $localize`:@@auth.professional.mainJob.placeholder:Activity`;

    this.mainJobControl = new UntypedFormControl(
      null,
      [Validators.required],
    );
    this.countryControl = new UntypedFormControl(
        this.country || 'FR',
        [Validators.required],
    );
    this.companyRegNumberControl = new UntypedFormControl(
        null,
        [Validators.required, Validators.pattern(/^[0-9]{14}$/)], [RegistrationNumber.valid(this.professionalPrivateService)],
    );
    this.usernameControl = new UntypedFormControl(
      '',
      [...usernameValidators],
      uniqueField.bind(this, 'username', null),
    );
    this.brandControl = new UntypedFormControl(
      '',
      [...brandValidators],
    );
    this.pseudoControl = new UntypedFormControl(
      '',
      [...pseudoValidators],
      [
        uniqueField.bind(this, 'username', null),
        uniqueFieldPro.bind(this, 'pseudo', ''),
      ],
    );
    this.plainPasswordControl = new UntypedFormControl(
      '',
      [Validators.required, Validators.pattern(/^(?!.* )(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/)],
    );
    this.signupForm = this.fb.group({
      accountType: ['particular', Validators.required],
      professional: [false, Validators.required],
      firstName: ['', [...firstNameValidators]],
      lastName: ['', [...lastNameValidators]],
      email: ['', [
        ...emailValidators],
        uniqueField.bind(this, 'email', null),
      ],
      plainPassword: this.plainPasswordControl,
      username: this.usernameControl,
      optInPartnerEmail: [false],
      cgu: [false, Validators.requiredTrue],
    });
  }

  ngOnInit(): void {
    this.watchFieldProfesionnal();
    this.loadUser();
    this.loadProfessionalJob();
    this.loadCountries();
    if (this.route.snapshot.url.some((urlSegment) => urlSegment.path === 'pro')) {
      this.signupForm.patchValue({professional: true});
      this.signupForm.patchValue({accountType: 'professional'});
    }
    if (this.route.snapshot.queryParams && this.route.snapshot.queryParams.email) {
      this.signupForm.get('email').patchValue(this.route.snapshot.queryParams.email);
      this.signupForm.get('emailConfirmation').patchValue(this.route.snapshot.queryParams.email);
      this.emailReadOnly = true;
    }
  }

  loadUser(): void {
    // this.currentUser = {} as any;
    // this.signupForm.removeControl('plainPassword');
    // this.signupForm.removeControl('emailConfirmation');


    this.userStoreSubscription = combineLatest([
      this.userStoreService.userStore.pipe(filter((u) => !!u)),
      this.userStoreService.userStoreProfessional,
    ]).pipe(
      switchMap(([userConnected, proConnected]) => {
        if (userConnected.professionalNew && !userConnected.professionalId && !proConnected) {
          // if user has start a pro signup, but it has crashed before setting user.professionalId
          return this.professionalService.getProfessionalByUser(userConnected['@id']).pipe(
            map((proCollection) => proCollection['hydra:member'].pop()),
            map((orphanPro) => {
                 return {user: userConnected, pro: orphanPro}
            })
          )
        } else {
          return of({user: userConnected, pro: proConnected} as { user: User, pro: Professional });
        }
      }),
      tap(({user, pro}) => {
        this.currentUser = user;
        this.currentPro = pro;
        if(!this.initiated) {
          this.signupForm.removeControl('plainPassword');
          this.signupForm.patchValue({
            professional: this.currentUser.accountType === 'professional' || this.currentUser.professional || this.currentUser.professionalNew,
            accountType: this.currentUser.accountType ||
                ((this.currentUser.professional || this.currentUser.professionalNew) ? 'professional': 'particular'),
            username: this.currentUser.username,
            email: this.currentUser.email,
            emailConfirmation: this.currentUser.email,
            lastName: this.currentUser.lastName,
            firstName: this.currentUser.firstName,
            optInPartnerEmail: this.currentUser.optInPartnerEmail,
            cgu: true,
          });
          if (this.error) {
            const x = handleHydraError(this.signupForm as UntypedFormGroup, this.error,
              $localize`:@@toast.could_not_save_pro_private:Can not save private information (email & phone)`);
            this.error = null;
          }
        }
        this.initiated = true;
      }),
    ).subscribe();
  }

  watchFieldProfesionnal(): void {
    this.professionalFieldSubscription = this.signupForm.get('accountType').valueChanges.pipe(
        tap((accountType) => {
          if (accountType === 'professional') {
            this.signupForm.patchValue({professional: true});
            this.signupForm.removeControl('username');
            this.signupForm.addControl('brand', this.brandControl);
            this.signupForm.addControl('pseudo', this.pseudoControl);
            this.signupForm.addControl('mainJob', this.mainJobControl);
            this.signupForm.addControl('country', this.countryControl);
            if (this.countryControl.value === 'FR' && !this.signupForm.get('registrationNumber')) {
              this.signupForm.addControl('registrationNumber', this.companyRegNumberControl);
            }
            if (this.currentUser && this.currentUser.username) {
              this.signupForm.get('pseudo').setValue(this.currentUser.username);

              if (this.currentPro && this.currentPro.pseudo) {
                this.signupForm.get('pseudo').setAsyncValidators(
                    [
                      uniqueField.bind(this, 'username', this.currentUser.username),
                      uniqueFieldPro.bind(this, 'pseudo', this.currentPro.pseudo),
                    ]);
              }
            }
            if (this.currentPro && this.currentPro.mainJob) {
              this.signupForm.get('mainJob').patchValue(typeof this.currentPro.mainJob === 'string' ?
                  this.currentPro.mainJob : this.currentPro.mainJob['@id']);
            }
            if (this.currentPro && this.currentPro.brand) {
              this.signupForm.get('brand').patchValue(typeof this.currentPro.brand === 'string' ?
                  this.currentPro.brand : this.currentPro.brand['@id']);
            }
            if (this.currentPro && this.currentPro.country) {
              this.signupForm.get('country').patchValue(this.currentPro.country);
            }
            if (this.currentPro && this.countryControl.value === 'FR' && this.currentPro.private && typeof this.currentPro.private !== 'string') {
              this.signupForm.get('registrationNumber').patchValue(this.currentPro.private.registrationNumber);
            }
          } else {
            this.signupForm.patchValue({professional: false});
            this.signupForm.removeControl('brand');
            this.signupForm.removeControl('pseudo');
            this.signupForm.removeControl('mainJob');
            this.signupForm.removeControl('country');
            this.signupForm.removeControl('registrationNumber');
            this.signupForm.addControl('username', this.usernameControl);
          }
        }),
        filter((accountType) => accountType === 'professional'),
        switchMap(() => {
          return this.signupForm.controls['country'].valueChanges.pipe(
              tap((country) => {
                (country === 'FR')
                    ? this.signupForm.addControl('registrationNumber', this.companyRegNumberControl)
                    : this.signupForm.removeControl('registrationNumber');
              })
          );
        })
    ).subscribe();
  }

  public isFieldInvalid(name: string): boolean {
    if (!this.signupForm) {
      return false;
    }

    const control = this.signupForm.get(name);
    if (!control) {
      return false;
    }
    return (this.hasError || control.dirty || control.touched) && (control.invalid || control.pending);
  }

  public suggestPseudo(event) {
    if (this.signupForm.get('pseudo').value === '') {
      this.signupForm.get('pseudo').setValue(this.slugifyPipe.transform(event.target.value));
      this.signupForm.get('pseudo').markAsDirty();
    }
  }

  save(): void {
    this.error = null;
    if (this.signupForm.valid) {
      this.submitted = true;
      const updateAction = !!this.currentUser;
      const request = (updateAction) ? this.updateKozikaza({'@id': this.currentUser['@id']}) : this.signupKozikaza();

      this.signupSubscription = request
        .pipe(
          catchError((err) => {
            this.updateCurrentFromError(err);
            return throwError(err);
          }),
          finalize(() => {
            this.submitted = false;
          }),
          tap((result: any) => {
            if (updateAction) {
              this.authenticationService.updateMe();
            }
          }),
        )
        .subscribe(
          () => {
            const event = (this.signupForm.get('professional').value) ? 'createProAccount'
              : (this.signupForm.get('accountType').value === 'school' ? 'createSchoolAccount'
                : (this.signupForm.get('accountType').value === 'professional' ? 'createProAccount' : 'createAccount'));
            this.dataLayerService.sendEvent({
              event,
              eventCategory: 'account',
              eventAction: 'create',
              eventLabel: this.dataLayerService.getCurrentContext(),
            });
          },
          (err) => {
            this.error = err.error;
            this.toastrService.error(handleHydraError(this.signupForm as UntypedFormGroup, err?.error,
              $localize`:@@toast.could_not_save_pro_private:Can not save private information (email & phone)`),
            );
          },
        );
    } else {
      this.hasError = true;
    }
  }

  signupKozikaza(): Observable<{ user: User, pro?: Professional }> {
    const fields = this.getKozikazaFields();
    const isProfessionalRegister = this.signupForm.get('accountType').value === 'professional'
        || (this.signupForm.get('professional') && this.signupForm.get('professional').value);
    const fieldsPro = (isProfessionalRegister) ? this.getProfessionalFields() : null;
    return this.authenticationService.signup(fields, fieldsPro).pipe(
        switchMap((result: {user: User, pro?: Professional}) => {
          if (result.pro && result.pro.pseudo) {
            this.signupForm.get('pseudo').setAsyncValidators(
                [
                  uniqueField.bind(this, 'username', result.user.username),
                  uniqueFieldPro.bind(this, 'pseudo', result.pro.pseudo),
                ]);
          }
          return (isProfessionalRegister && result.pro) ? this.updateProPrivate(result) : of(result);
        })
    );
  }

  updateKozikaza(extrafields: Partial<User> = {}): Observable<{ user: User, pro?: Professional }> {
    const fields = Object.assign(extrafields, this.getKozikazaFields()) as Partial<User>;

    const isProfessionalRegister = (this.signupForm.get('professional') && this.signupForm.get('professional').value)
    || (this.signupForm.get('accountType') && this.signupForm.get('accountType').value === 'professional');

    return this.legacyUserService.update(fields['@id'], {...fields}, !isProfessionalRegister).pipe(
      switchMap((user: User) => {
        return (!isProfessionalRegister) ? of({user}) :
          ((this.currentUser && (this.currentUser.professionalId || this.currentPro)) ?
              // if pro exists, update pro
              this.updateProfessional(user, this.currentUser.professionalId || this.currentPro).pipe(
                catchError((err) => {
                  this.updateCurrentFromError(err);
                  return throwError(err);
                }),
              ) :
              // if pro does not exists, create pro
              this.signupProfessional(user).pipe(
                catchError((err) => {
                  this.updateCurrentFromError(err);
                  return throwError(err);
                }),
              )
          ).pipe(
              switchMap((result: {user: User, pro?: Professional}) =>
                  (isProfessionalRegister && result.pro) ? this.updateProPrivate(result) : of(result)),
              tap((result) => {
                this.userStoreService.userProfessional = result.pro;
              }),
          );
      }),
    );
  }

  private updateProPrivate(userProResult: {user: User, pro?: Professional}): Observable<{ user: User, pro?: Professional }> {
    const putPrivate = { '@id': typeof userProResult.pro.private === 'string' ? userProResult.pro.private : userProResult.pro.private['@id'], ...this.professionalPrivateFields};
    return this.professionalPrivateService.putProfessionalPrivate(putPrivate).pipe(
        switchMap((proPrivate) => {
          userProResult.pro.private = proPrivate;
          return of<{user: User, pro?: Professional}>(userProResult);
        })
    );

  }

  private updateCurrentFromError(err) {
    if (err?.user) {
      this.currentUser = err.user;
    }
    if (err?.pro) {
      this.currentPro = err.pro;
    }
  }

  signupProfessional(user: User): Observable<{ user: User, pro?: Professional }> {
    const fields = this.getProfessionalFields();
    return this.authenticationService.signupProfessional(
      fields,
      user['@id'],
      (typeof user.activeKaza === 'string') ? user.activeKaza : user.activeKaza['@id'],
      user[`reach5Id`],
    );
  }

  updateProfessional(user: User, pro: Professional | string): Observable<{ user: User, pro?: Professional }> {
    const fields = this.getProfessionalFields();
    return this.professionalService.putProfessional({
      '@id': typeof pro === 'string' ? pro : pro['@id'],
      ...fields,
      socialKozikazaUserId: user['@id'],
      socialKozikazaKazaId: (typeof user.activeKaza === 'string') ? user.activeKaza : user.activeKaza['@id'],
    }).pipe(
      map((proUpdated) => ({user, pro: proUpdated})),
      switchMap((result) => {
        return (this.currentUser.professionalId || !result.pro) ?
          of(result) :
          this.userService.putUser({'@id': this.currentUser['@id'], professionalId: `${ result.pro['@id'] }`}).pipe(
            map((userUpdated) => ({user: userUpdated, pro: result.pro})),
          );
      }),
    );
  }

  loadProfessionalJob(): void {
    this.professionalJobSubscription = this.professionalJobService.getProfessionalJobs()
      .pipe(
        map((proJobs) => proJobs['hydra:member']),
        tap((proJobs) => this.proJobsList = proJobs),
      )
      .subscribe();
  }

  loadCountries(): void {

    this.countriesSubscription = this.sdkCountryLegacyService.getCountries().pipe(
        tap((data) => {
          this.countriesList = data;
        })
    ).subscribe();
  }

  trackById(index, data): number {
    return data ? data['@id'] : undefined;
  }

  trackByCode(index, data): number {
    return data ? data.code : undefined;
  }

  close(): void {
    this.authPopinService.close(true);
  }

  getKozikazaFields(): Partial<RegisterUser> {
    const {
      firstName,
      lastName,
      email,
      plainPassword,
      optInPartnerEmail,
      accountType,
    }: Partial<RegisterUser> = this.signupForm.value;
    // set KZP partnerKey
    const partnerKey = this.kzpService.getPartnerKeyFromCookie();
    return {
      firstName,
      lastName,
      email,
      plainPassword,
      optInPartnerEmail,
      accountType,
      partnerKey,
      professionalNew: (this.signupForm.get('professional') && this.signupForm.get('professional').value) ? true : false,
      username: (this.signupForm.get('pseudo')) ? this.signupForm.get('pseudo').value : this.signupForm.get('username').value,
      firstPageSeen: this.cookieService.get('firstPageSeen') || undefined,
      firstPageSeenDataLayerTemplate: this.cookieService.get('firstPageSeen_template') || undefined,
      firstPageSeenDataLayerLanguage: this.cookieService.get('firstPageSeen_language') || undefined,
      firstPageSeenDataLayerParams: this.cookieService.get('firstPageSeen_params') || undefined,
      smartPriceModalViewed: optInPartnerEmail === true,
    };
  }

  getProfessionalFields(): Partial<Professional> {
    const {
      email,
      brand,
      mainJob,
      country,
    }: Partial<Professional> = this.signupForm.value;

    return {
      email,
      brand,
      mainJob,
      country,
    };
  }

  get professionalPrivateFields(): Partial<ProfessionalPrivate> {
    const {
      registrationNumber,
    }: Partial<ProfessionalPrivate> = this.signupForm.value;

    return {
      registrationNumber,
    };
  }

  ngOnDestroy(): void {

    if(this.signupForm.get('username')) {
      this.signupForm.controls['username'].clearAsyncValidators();
    }
    if(this.signupForm.get('pseudo')) {
      this.signupForm.controls['pseudo'].clearAsyncValidators();
    }
    if(this.signupForm.get('email')) {
      this.signupForm.controls['email'].clearAsyncValidators();
    }
    if (this.signupSubscription && !this.signupSubscription.closed) {
      this.signupSubscription.unsubscribe();
    }

    if (this.userStoreSubscription && !this.userStoreSubscription.closed) {
      this.userStoreSubscription.unsubscribe();
    }
    if (this.professionalFieldSubscription && !this.professionalFieldSubscription.closed) {
      this.professionalFieldSubscription.unsubscribe();
    }
    if (this.professionalJobSubscription && !this.professionalJobSubscription.closed) {
      this.professionalJobSubscription.unsubscribe();
    }
    if (this.countriesSubscription && !this.countriesSubscription.closed) {
      this.countriesSubscription.unsubscribe();
    }
  }
}
